/**
 * Theory component for product card video lazy loading
 * Handles hover-based video loading for product cards to improve page performance
 */

class TdProductCard {
  constructor(card) {
    this.card = card;
    this.container = card.querySelector('.td-product-card__image--secondary[data-video-src]');
    this.video = this.container?.querySelector('video');
    this.videoLoaded = false;
    
    /* Store video event listeners for cleanup */
    this.videoEventHandlers = [];
    
    if (!this.video || !this.container) return;
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.card.addEventListener('mouseenter', () => this.loadVideo());
    this.card.addEventListener('mouseleave', () => this.pauseVideo());
    this.card.addEventListener('touchstart', () => this.loadVideo(), { passive: true });
    this.card.addEventListener('touchend', () => this.pauseVideo(), { passive: true });
    this.card.addEventListener('touchcancel', () => this.pauseVideo(), { passive: true });
  }

  pauseVideo() {
    if (this.video) {
      this.video.pause();
      this.video.currentTime = 0;
      
      /* Remove pending video event listeners to prevent them from firing */
      this.videoEventHandlers.forEach(({ type, handler }) => {
        this.video.removeEventListener(type, handler);
      });
      this.videoEventHandlers = [];
    }
    
    if (this.container) {
      this.container.classList.remove('td-product-card__image--secondary--loading');
    }
  }

  loadVideo() {
    if (!this.video || !this.container) return;
    
    /* Remove any pending video event listeners from previous load attempts */
    this.videoEventHandlers.forEach(({ type, handler }) => {
      this.video.removeEventListener(type, handler);
    });
    this.videoEventHandlers = [];
    
    const videoSrc = this.container.dataset.videoSrc;
    if (!videoSrc) return;
    
    this.container.classList.add('td-product-card__image--secondary--loading');
    
    const source = this.video.querySelector('source');
    if (!source) return;
    
    const currentSrc = source.getAttribute('src');
    if (!currentSrc || currentSrc !== videoSrc) {
      source.setAttribute('src', videoSrc);
      this.videoLoaded = false;
    }
    
    const playVideo = async () => {
      if (!this.video) return;
      try {
        await this.video.play();
        this.video.classList.add('td-product-card__video--loaded');
        this.video.removeAttribute('poster');
      } catch (error) {
        console.warn('Video play failed:', error);
      }
    };
    
    if (this.videoLoaded && this.video.readyState >= 3) {
      playVideo();
    } else if (!this.videoLoaded) {
      this.videoLoaded = true;
      this.video.load();
      
      const handleCanPlay = () => playVideo();
      if (this.video.readyState >= 3) {
        handleCanPlay();
      } else {
        this.video.addEventListener('canplay', handleCanPlay, { once: true });
        this.videoEventHandlers.push({ type: 'canplay', handler: handleCanPlay });
      }
      
      const handleError = () => {
        this.videoLoaded = false;
        if (this.video) {
          this.video.classList.remove('td-product-card__video--loaded');
        }
        if (this.container) {
          this.container.classList.remove('td-product-card__image--secondary--loading');
        }
      };
      this.video.addEventListener('error', handleError, { once: true });
      this.videoEventHandlers.push({ type: 'error', handler: handleError });
    } else {
      const handleCanPlay = () => playVideo();
      this.video.addEventListener('canplay', handleCanPlay, { once: true });
      this.videoEventHandlers.push({ type: 'canplay', handler: handleCanPlay });
      
      const handleError = () => {
        this.videoLoaded = false;
        if (this.video) {
          this.video.classList.remove('td-product-card__video--loaded');
        }
        if (this.container) {
          this.container.classList.remove('td-product-card__image--secondary--loading');
        }
      };
      this.video.addEventListener('error', handleError, { once: true });
      this.videoEventHandlers.push({ type: 'error', handler: handleError });
    }
  }
}

if (!customElements.get('td-product-card-controller')) {
  class TdProductCardController extends HTMLElement {
    connectedCallback() {
      this.initializeCards();
      this.setupObserver();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    initializeCards() {
      const cards = document.querySelectorAll('.td-product-card:not([data-video-initialized])');
      cards.forEach((card) => {
        const hasVideo = card.querySelector('.td-product-card__image--secondary[data-video-src] video');
        if (!hasVideo) return;
        
        card.setAttribute('data-video-initialized', '');
        new TdProductCard(card);
      });
    }

    setupObserver() {
      this.observer = new MutationObserver(() => {
        this.initializeCards();
      });
      
      this.observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  customElements.define('td-product-card-controller', TdProductCardController);

  // Auto-initialize the controller when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeController);
  } else {
    initializeController();
  }

  function initializeController() {
    if (!document.querySelector('td-product-card-controller')) {
      const controller = document.createElement('td-product-card-controller');
      document.body.appendChild(controller);
    }
  }
}
